// src/store/slices/clientsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  clientService,
  Client,
  ClientFormData,
  Location,
  LocationFormData,
  Asset,
  AssetFormData,
  LocationType,
  LocationTypeFormData,
  AssetType,
  AssetTypeFormData,
  AssetCategory,
  getAssetById
} from '../../services/clientService';

// Define data interfaces for pagination
interface ClientsData {
  result: Client[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface LocationsData {
  result: Location[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface AssetsData {
  result: Asset[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface Pagination {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface ClientsState {
  clients: Client[];
  clientLocations: Location[];
  assetLocations: Location[]; // Add this new property
  locationAssets: Asset[];
  flowStationAssets: Asset[];
  allAssets: Asset[];
  locationTypes: LocationType[];
  assetTypes: AssetType[];
  assetCategory: AssetCategory[];
  selectedLocation: Location | null;
  selectedAsset: Asset | null;
  pagination: Pagination | null;
  locationsPagination: Pagination | null;
  assetsPagination: Pagination | null;
  allAssetsPagination: Pagination | null;
  isLoading: boolean;
  isLocationsLoading: boolean;
  isAssetsLoading: boolean;
  isAllAssetsLoading: boolean;
  isTypesLoading: boolean;
  isCategoryLoading: boolean;
  isAssetLocationsLoading: boolean; // Add this new loading state
  assetDetails: {
    asset: Asset | null;
    maintenance: any[];
    archive: any[];
  } | null;
  isAssetDetailsLoading: boolean;
  isFlowStationAssetsLoading: boolean;
  error: string | null;
}

const initialState: ClientsState = {
  clients: [],
  clientLocations: [],
  assetLocations: [], // Initialize new property
  locationAssets: [],
  flowStationAssets: [],
  allAssets: [],
  locationTypes: [],
  assetTypes: [],
  assetCategory: [],
  selectedLocation: null,
  selectedAsset: null,
  pagination: null,
  locationsPagination: null,
  assetsPagination: null,
  allAssetsPagination: null,
  isLoading: false,
  isLocationsLoading: false,
  isAssetsLoading: false,
  isAllAssetsLoading: false,
  isTypesLoading: false,
  isCategoryLoading: false,
  isAssetLocationsLoading: false, // Initialize new loading state
  assetDetails: null,
  isAssetDetailsLoading: false,
  isFlowStationAssetsLoading: false,
  error: null,
};

// Client actions
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await clientService.getAllClients(page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

export const createClient = createAsyncThunk(
  'clients/createClient',
  async (clientData: ClientFormData, { rejectWithValue }) => {
    try {
      const client = await clientService.createClient(clientData);
      return client;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create client');
    }
  }
);

export const updateClient = createAsyncThunk(
  'clients/updateClient',
  async ({ id, clientData }: { id: string; clientData: Partial<ClientFormData> }, { rejectWithValue }) => {
    try {
      const response = await clientService.updateCustomer(id, clientData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update customer');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'clients/deleteClient',
  async (id: string, { rejectWithValue }) => {
    try {
      await clientService.deleteClient(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
    }
  }
);

// Location actions
export const fetchClientLocations = createAsyncThunk(
  'clients/fetchClientLocations',
  async ({ clientId, page = 1, limit = 10 }: { clientId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await clientService.getClientLocations(clientId, page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client locations');
    }
  }
);

export const fetchLocationDetails = createAsyncThunk(
  'clients/fetchLocationDetails',
  async (locationId: string, { rejectWithValue }) => {
    try {
      const response = await clientService.getLocationDetails(locationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location details');
    }
  }
);

export const createLocation = createAsyncThunk(
  'clients/createLocation',
  async (locationData: LocationFormData, { rejectWithValue }) => {
    try {
      const location = await clientService.createLocation(locationData);
      return location;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create location');
    }
  }
);

export const updateLocation = createAsyncThunk(
  'clients/updateLocation',
  async ({ id, data }: { id: string; data: LocationFormData }, { rejectWithValue }) => {
    try {
      const location = await clientService.updateLocation(id, data);
      return location;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update location');
    }
  }
);

export const deleteLocation = createAsyncThunk(
  'clients/deleteLocation',
  async (locationId: string, { rejectWithValue }) => {
    try {
      await clientService.deleteLocation(locationId);
      return locationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete location');
    }
  }
);

// Asset actions
export const fetchLocationAssets = createAsyncThunk(
  'clients/fetchLocationAssets',
  async ({ locationId, page = 1, limit = 10 }: { locationId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await clientService.getLocationAssets(locationId, page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location assets');
    }
  }
);

export const createAsset = createAsyncThunk(
  'clients/createAsset',
  async (assetData: AssetFormData, { rejectWithValue }) => {
    try {
      const asset = await clientService.createAsset(assetData);
      return asset;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create asset');
    }
  }
);

// Add this action to update an asset
export const updateAsset = createAsyncThunk(
  'clients/updateAsset',
  async ({ id, data }: { id: string; data: AssetFormData }, { rejectWithValue }) => {
    try {
      const response = await clientService.updateAsset(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update asset');
    }
  }
);

export const deleteAsset = createAsyncThunk(
  'clients/deleteAsset',
  async (id: string, { rejectWithValue }) => {
    try {
      await clientService.deleteAsset(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset');
    }
  }
);

// New async thunks for fetching all assets and single asset
export const fetchAllAssets = createAsyncThunk(
  'clients/fetchAllAssets',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await clientService.getAllAssets(page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assets');
    }
  }
);

export const fetchAsset = createAsyncThunk(
  'clients/fetchAsset',
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = await clientService.getAsset(assetId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset details');
    }
  }
);

// Add this new async thunk
export const fetchAssetsByClient = createAsyncThunk(
  'clients/fetchAssetsByClient',
  async ({ clientId, page = 1, limit = 10 }: { clientId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await clientService.getAssetsByClient(clientId, page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch client assets');
    }
  }
);

export const fetchAssetsByFlowStation = createAsyncThunk(
  'clients/fetchAssetsByFlowStation',
  async ({ flowStationId, page = 1, limit = 100 }: { flowStationId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await clientService.getAssetsByFlowStation(flowStationId, page, limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch flow station assets');
    }
  }
);

// Type actions
export const fetchLocationTypes = createAsyncThunk(
  'clients/fetchLocationTypes',
  async (_, { rejectWithValue }) => {
    try {
      const types = await clientService.getLocationTypes();
      return types;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location types');
    }
  }
);

export const createLocationType = createAsyncThunk(
  'clients/createLocationType',
  async (typeData: LocationTypeFormData, { rejectWithValue }) => {
    try {
      const response = await clientService.createLocationType(typeData);
      // Return just the data property, not the entire response
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create location type');
    }
  }
);

export const fetchAssetTypes = createAsyncThunk(
  'clients/fetchAssetTypes',
  async (_, { rejectWithValue }) => {
    try {
      const types = await clientService.getAssetTypes();
      return types;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset types');
    }
  }
);

export const createAssetType = createAsyncThunk(
  'clients/createAssetType',
  async (typeData: AssetTypeFormData, { rejectWithValue }) => {
    try {
      const type = await clientService.createAssetType(typeData);
      return type;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create asset type');
    }
  }
);

export const fetchAssetCategory = createAsyncThunk(
  'clients/fetchAssetCategory',
  async (_, { rejectWithValue }) => {
    try {
      const categories = await clientService.getAssetCategory();
      return categories;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset categories');
    }
  }
);

export const createAssetCategory = createAsyncThunk(
  'clients/createAssetCategory',
  async (categoryData: AssetCategory, { rejectWithValue }) => {
    try {
      const type = await clientService.createAssetCategory(categoryData);
      return type;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create asset category');
    }
  }
);

// Add this new async thunk for fetching asset locations
export const fetchAssetLocations = createAsyncThunk(
  'clients/fetchAssetLocations',
  async (_, { rejectWithValue }) => {
    try {
      const locations = await clientService.getAssetLocations();
      return locations;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset locations');
    }
  }
);

// Add delete async thunks
export const deleteAssetType = createAsyncThunk(
  'clients/deleteAssetType',
  async (id: string, { rejectWithValue }) => {
    try {
      await clientService.deleteAssetType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset type');
    }
  }
);

export const deleteLocationType = createAsyncThunk(
  'clients/deleteLocationType',
  async (id: string, { rejectWithValue }) => {
    try {
      await clientService.deleteLocationType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete location type');
    }
  }
);

export const deleteAssetCategory = createAsyncThunk(
  'clients/deleteAssetCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await clientService.deleteAssetCategory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete asset category');
    }
  }
);

// Add the async thunk for fetching asset details
export const fetchAssetDetails = createAsyncThunk(
  'clients/fetchAssetDetails',
  async (assetId: string) => {
    const response = await getAssetById(assetId);
    return response;
  }
);

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedLocation: (state, action: PayloadAction<Location | null>) => {
      state.selectedLocation = action.payload;
    },
    clearSelectedLocation: (state) => {
      state.selectedLocation = null;
    },
    setSelectedAsset: (state, action: PayloadAction<Asset | null>) => {
      state.selectedAsset = action.payload;
    },
    clearSelectedAsset: (state) => {
      state.selectedAsset = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // FETCH operations - ONLY these should set the global error state
      
      // Fetch clients cases
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.clients = action.payload;
          state.pagination = null;
        } else if (action.payload && 'result' in action.payload) {
          const data = action.payload as ClientsData;
          state.clients = data.result;
          state.pagination = {
            totalCount: data.totalCount,
            currentPage: data.currentPage,
            totalPages: data.totalPages,
            previousPage: data.previousPage,
            nextPage: data.nextPage
          };
        } else {
          state.clients = [];
          state.pagination = null;
        }
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch client locations cases
      .addCase(fetchClientLocations.pending, (state) => {
        state.isLocationsLoading = true;
        state.error = null;
      })
      .addCase(fetchClientLocations.fulfilled, (state, action) => {
        state.isLocationsLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (Array.isArray(action.payload)) {
          state.clientLocations = action.payload;
          state.locationsPagination = null;
        } else if (action.payload && 'result' in action.payload) {
          const data = action.payload as LocationsData;
          state.clientLocations = data.result;
          state.locationsPagination = {
            totalCount: data.totalCount,
            currentPage: data.currentPage,
            totalPages: data.totalPages,
            previousPage: data.previousPage,
            nextPage: data.nextPage
          };
        } else {
          state.clientLocations = [];
          state.locationsPagination = null;
        }
      })
      .addCase(fetchClientLocations.rejected, (state, action) => {
        state.isLocationsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch location details cases
      .addCase(fetchLocationDetails.pending, (state) => {
        state.isLocationsLoading = true;
        state.error = null;
      })
      .addCase(fetchLocationDetails.fulfilled, (state, action) => {
        state.isLocationsLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.selectedLocation = action.payload;
      })
      .addCase(fetchLocationDetails.rejected, (state, action) => {
        state.isLocationsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch location assets cases
      .addCase(fetchLocationAssets.pending, (state) => {
        state.isAssetsLoading = true;
        state.error = null;
      })
      .addCase(fetchLocationAssets.fulfilled, (state, action) => {
        state.isAssetsLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (Array.isArray(action.payload)) {
          state.locationAssets = action.payload;
          state.assetsPagination = null;
        } else if (action.payload && 'result' in action.payload) {
          const data = action.payload as AssetsData;
          state.locationAssets = data.result;
          state.assetsPagination = {
            totalCount: data.totalCount,
            currentPage: data.currentPage,
            totalPages: data.totalPages,
            previousPage: data.previousPage,
            nextPage: data.nextPage
          };
        } else {
          state.locationAssets = [];
          state.assetsPagination = null;
        }
      })
      .addCase(fetchLocationAssets.rejected, (state, action) => {
        state.isAssetsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch all assets cases
      .addCase(fetchAllAssets.pending, (state) => {
        state.isAllAssetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAllAssets.fulfilled, (state, action) => {
        state.isAllAssetsLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (Array.isArray(action.payload)) {
          state.allAssets = action.payload;
          state.allAssetsPagination = null;
        } else if (action.payload && 'result' in action.payload) {
          const data = action.payload as AssetsData;
          state.allAssets = data.result;
          state.allAssetsPagination = {
            totalCount: data.totalCount,
            currentPage: data.currentPage,
            totalPages: data.totalPages,
            previousPage: data.previousPage,
            nextPage: data.nextPage
          };
        } else {
          state.allAssets = [];
          state.allAssetsPagination = null;
        }
      })
      .addCase(fetchAllAssets.rejected, (state, action) => {
        state.isAllAssetsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch single asset cases
      .addCase(fetchAsset.pending, (state) => {
        state.isAllAssetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAsset.fulfilled, (state, action) => {
        state.isAllAssetsLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.selectedAsset = action.payload;
      })
      .addCase(fetchAsset.rejected, (state, action) => {
        state.isAllAssetsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch assets by client cases
      .addCase(fetchAssetsByClient.pending, (state) => {
        state.isAllAssetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetsByClient.fulfilled, (state, action) => {
        state.isAllAssetsLoading = false;
        
        if (Array.isArray(action.payload)) {
          state.allAssets = action.payload;
          state.allAssetsPagination = null;
        } else {
          state.allAssets = action.payload.result || [];
          state.allAssetsPagination = {
            totalCount: action.payload.totalCount,
            currentPage: action.payload.currentPage,
            totalPages: action.payload.totalPages,
            previousPage: action.payload.previousPage,
            nextPage: action.payload.nextPage
          };
        }
      })
      .addCase(fetchAssetsByClient.rejected, (state, action) => {
        state.isAllAssetsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchAssetsByFlowStation.pending, (state) => {
        state.isFlowStationAssetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetsByFlowStation.fulfilled, (state, action) => {
        state.isFlowStationAssetsLoading = false;
        if (Array.isArray(action.payload)) {
          state.flowStationAssets = action.payload;
        } else if (action.payload && 'result' in action.payload) {
          state.flowStationAssets = (action.payload as any).result || [];
        } else {
          state.flowStationAssets = [];
        }
      })
      .addCase(fetchAssetsByFlowStation.rejected, (state, action) => {
        state.isFlowStationAssetsLoading = false;
        state.error = action.payload as string;
      })

      // Fetch location types cases
      .addCase(fetchLocationTypes.pending, (state) => {
        state.isTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchLocationTypes.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (action.payload && typeof action.payload === 'object' && 'data' in action.payload) {
          state.locationTypes = action.payload.data.result || [];
        } else if (Array.isArray(action.payload)) {
          state.locationTypes = action.payload;
        } else {
          state.locationTypes = [];
        }
      })
      .addCase(fetchLocationTypes.rejected, (state, action) => {
        state.isTypesLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch asset types cases
      .addCase(fetchAssetTypes.pending, (state) => {
        state.isTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetTypes.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (action.payload && typeof action.payload === 'object' && 'data' in action.payload) {
          state.assetTypes = action.payload.data.result || [];
        } else if (Array.isArray(action.payload)) {
          state.assetTypes = action.payload;
        } else {
          state.assetTypes = [];
        }
      })
      .addCase(fetchAssetTypes.rejected, (state, action) => {
        state.isTypesLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch asset category cases
      .addCase(fetchAssetCategory.pending, (state) => {
        state.isCategoryLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetCategory.fulfilled, (state, action) => {
        state.isCategoryLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        if (action.payload && action.payload.data && 'result' in action.payload.data) {
          state.assetCategory = action.payload.data.result;
        } else {
          state.assetCategory = [];
        }
      })
      .addCase(fetchAssetCategory.rejected, (state, action) => {
        state.isCategoryLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch asset locations cases
      .addCase(fetchAssetLocations.pending, (state) => {
        state.isAssetLocationsLoading = true;
        state.error = null;
      })
      .addCase(fetchAssetLocations.fulfilled, (state, action) => {
        state.isAssetLocationsLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.assetLocations = action.payload;
      })
      .addCase(fetchAssetLocations.rejected, (state, action) => {
        state.isAssetLocationsLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })

      // Fetch asset details cases
      .addCase(fetchAssetDetails.pending, (state) => {
        state.isAssetDetailsLoading = true;
      })
      .addCase(fetchAssetDetails.fulfilled, (state, action) => {
        state.isAssetDetailsLoading = false;
        state.assetDetails = action.payload;
      })
      .addCase(fetchAssetDetails.rejected, (state) => {
        state.isAssetDetailsLoading = false;
        state.assetDetails = null;
      })

      // CREATE operations - these should NOT set the global error state
      
      // Create client cases
      .addCase(createClient.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here - keep fetch errors visible
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients.push(action.payload);
        // Don't modify error state
      })
      .addCase(createClient.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // Create location cases
      .addCase(createLocation.pending, (state) => {
        state.isLocationsLoading = true;
        // Don't clear the error state here
      })
      .addCase(createLocation.fulfilled, (state, action) => {
        state.isLocationsLoading = false;
        state.clientLocations.push(action.payload);
        // Don't modify error state
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.isLocationsLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // Update location cases
      .addCase(updateLocation.pending, (state) => {
        state.isLocationsLoading = true;
      })
      .addCase(updateLocation.fulfilled, (state, action) => {
        state.isLocationsLoading = false;

        const index = state.clientLocations.findIndex(loc => loc._id === action.payload._id);
        if (index !== -1) {
          state.clientLocations[index] = action.payload;
        }

        if (state.selectedLocation?._id === action.payload._id) {
          state.selectedLocation = action.payload;
        }
      })
      .addCase(updateLocation.rejected, (state) => {
        state.isLocationsLoading = false;
      })

      // Delete location cases
      .addCase(deleteLocation.pending, (state) => {
        state.isLocationsLoading = true;
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.isLocationsLoading = false;
        state.clientLocations = state.clientLocations.filter(loc => loc._id !== action.payload);
      })
      .addCase(deleteLocation.rejected, (state) => {
        state.isLocationsLoading = false;
      })

      // Create asset cases
      .addCase(createAsset.pending, (state) => {
        state.isAssetsLoading = true;
        // Don't clear the error state here
      })
      .addCase(createAsset.fulfilled, (state, action) => {
        state.isAssetsLoading = false;
        state.locationAssets.push(action.payload);
        // Don't modify error state
      })
      .addCase(createAsset.rejected, (state, action) => {
        state.isAssetsLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // Create location type cases
      .addCase(createLocationType.pending, (state) => {
        state.isTypesLoading = true;
        // Don't clear the error state here
      })
      .addCase(createLocationType.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.locationTypes.push(action.payload);
        // Don't modify error state
      })
      .addCase(createLocationType.rejected, (state, action) => {
        state.isTypesLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // Create asset type cases
      .addCase(createAssetType.pending, (state) => {
        state.isTypesLoading = true;
        // Don't clear the error state here
      })
      .addCase(createAssetType.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.assetTypes.push(action.payload);
        // Don't modify error state
      })
      .addCase(createAssetType.rejected, (state, action) => {
        state.isTypesLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // Create asset category cases
      .addCase(createAssetCategory.pending, (state) => {
        state.isCategoryLoading = true;
        // Don't clear the error state here
      })
      .addCase(createAssetCategory.fulfilled, (state, action) => {
        state.isCategoryLoading = false;
        state.assetCategory.push(action.payload);
        // Don't modify error state
      })
      .addCase(createAssetCategory.rejected, (state, action) => {
        state.isCategoryLoading = false;
        // Don't set error state - let the modal handle create errors
      })

      // UPDATE operations - these should NOT set the global error state
      
      // Update client cases
      .addCase(updateClient.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.clients.findIndex(client => client._id === action.payload._id);
        if (index !== -1) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update asset cases
      .addCase(updateAsset.pending, (state) => {
        state.isAssetsLoading = true;
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state, action) => {
        state.isAssetsLoading = false;
        // Update the asset in assetDetails if it exists
        if (state.assetDetails?.asset?._id === action.meta.arg.id) {
          state.assetDetails.asset = { ...state.assetDetails.asset, ...action.payload };
        }
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.isAssetsLoading = false;
        state.error = action.payload as string;
      })

      // Delete asset cases
      .addCase(deleteAsset.pending, (state) => {
        state.isAssetsLoading = true;
      })
      .addCase(deleteAsset.fulfilled, (state, action) => {
        state.isAssetsLoading = false;
        state.allAssets = state.allAssets.filter(a => a._id !== action.payload);
        state.locationAssets = state.locationAssets.filter(a => a._id !== action.payload);
      })
      .addCase(deleteAsset.rejected, (state) => {
        state.isAssetsLoading = false;
      })

      // DELETE operations - these should NOT set the global error state

      // Delete client cases
      .addCase(deleteClient.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = state.clients.filter(client => client._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      })

      // Delete asset type cases
      .addCase(deleteAssetType.pending, (state) => {
        state.isTypesLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteAssetType.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.assetTypes = state.assetTypes.filter(type => type._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteAssetType.rejected, (state, action) => {
        state.isTypesLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      })

      // Delete location type cases
      .addCase(deleteLocationType.pending, (state) => {
        state.isTypesLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteLocationType.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.locationTypes = state.locationTypes.filter(type => type._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteLocationType.rejected, (state, action) => {
        state.isTypesLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      })

      // Delete asset category cases
      .addCase(deleteAssetCategory.pending, (state) => {
        state.isCategoryLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteAssetCategory.fulfilled, (state, action) => {
        state.isCategoryLoading = false;
        state.assetCategory = state.assetCategory.filter(category => category._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteAssetCategory.rejected, (state, action) => {
        state.isCategoryLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      });
  },
});

export const { 
  clearError, 
  setSelectedLocation, 
  clearSelectedLocation,
  setSelectedAsset,
  clearSelectedAsset
} = clientsSlice.actions;
export default clientsSlice.reducer;
